import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';
import { ClientModes } from './clientModes';

export interface ClientRow {
  id: string;
  name: string;
  loadCount?: number;
  userCount?: number;
  deliveryCount?: number;
  /**
   * 🔴 **`status_id` NO EXISTE.** Ni en la tabla `clients`, ni en el modelo, ni en la respuesta del API
   * — comprobado contra `information_schema`: la tabla tiene `id`, `name`, `createdAt`, `updatedAt` y las
   * tres banderas de modo, nada más.
   *
   * La tarjeta lo leía y lo comparaba con `== null`, así que `undefined` daba **"Active" para los 206
   * clientes**, siempre, sin ningún dato detrás. Un indicador que nunca cambia no informa: decora.
   *
   * ⏭️ Se deja declarado para no romper el tipo mientras se agrega el campo de verdad
   * (`SQL_CLIENT_ACTIVE.sql`), y la tarjeta ya **no** dibuja nada cuando no viene: es mejor no mostrar el
   * estado que mostrar uno inventado.
   */
  status_id?: number;
  /** Estado real del cliente. Llega cuando `SQL_CLIENT_ACTIVE.sql` corrió y el modelo lo declara. */
  active?: boolean;
  /**
   * Modos a los que está enfocado el cliente.
   *
   * ⚠️ Opcionales porque un backend sin `SQL_CLIENT_MODES.sql` corrido no los manda. `modesOf` los lee
   * con respaldo en `true`, que es el estado de la base recién migrada; con respaldo en `false` la
   * pantalla afirmaría que ningún cliente tiene modo, que sería falso.
   *
   * ⚠️ **Llegan como `0`/`1`**, no como booleanos: la consulta de la lista usa `raw: true`, así que
   * Sequelize no convierte los `tinyint(1)`. Se tipan `boolean` porque es lo que significan, y `modesOf`
   * es quien normaliza — ver la nota de `flag()`, que es donde estaba el defecto.
   */
  mode_land?: boolean;
  mode_ocean?: boolean;
  mode_air?: boolean;
}

export const useClients = () => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/clients');
      setClients(res.data || []);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navRef.current('/login');
      else if (status === 403) navRef.current('/');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filtered = useMemo(
    () => clients.filter((c) => c.name?.toLowerCase().includes(search.trim().toLowerCase())),
    [clients, search]
  );

  const [creating, setCreating] = useState(false);
  /**
   * Alta de cliente, con su modo.
   *
   * 📌 Los modos van **siempre** en el cuerpo, incluso cuando son los tres en `true` —que es el valor
   * de partida del formulario y también el default de la base—. Mandarlos solo cuando difieren del
   * default obligaría a conocer ese default en dos lugares, y bastaría cambiarlo en la base para que el
   * alta empezara a guardar algo distinto de lo que muestra la pantalla.
   */
  const createClient = useCallback(
    async (name: string, modes: ClientModes): Promise<true | string> => {
      if (!name.trim()) return 'Name is required';
      setCreating(true);
      try {
        await axiosInstance.post('/clients', { name: name.trim(), ...modes });
        await fetchClients();
        return true;
      } catch (err: any) {
        return err?.response?.data?.message || 'Failed to create client';
      } finally {
        setCreating(false);
      }
    },
    [fetchClients]
  );

  return {
    clients: filtered,
    loading,
    creating,
    search,
    setSearch,
    createClient,
    openDetail: (id: string) => navRef.current(`/clients/${id}`),
  };
};
